/**
 * Bundle Price Update Script
 * Handles dynamic price calculation and display updates based on quantity changes
 */

// Debug flag - set to true to enable console logging
const DEBUG_PRICE_UPDATES = true;

// Debug logging function
function debugLog(...args) {
  if (DEBUG_PRICE_UPDATES) {
    console.log(...args);
  }
}

/**
 * Check if subscription tab is currently active
 * @returns {boolean} - True if subscription tab is active, false if one-time purchase tab is active
 */
function isSubscriptionTabActive() {
  // Method 1: Check radio button state (most reliable)
  const subscriptionRadio = document.querySelector('input#rc-radio2.rc-radio__input');
  if (subscriptionRadio && subscriptionRadio.checked) {
    debugLog('📋 Subscription tab detected via radio button (checked)');
    return true;
  }
  
  // Method 2: Check visibility classes as fallback
  const bundle = document.querySelector('#prvw__bundle_container');
  const subList = document.querySelector('.shopify_subscriptions_app_block_label_children');
  
  const isActive = bundle && bundle.classList.contains('prvw-hidden') && 
                   subList && subList.classList.contains('prvw-visible');
  
  if (isActive) {
    debugLog('📋 Subscription tab detected via visibility classes');
  } else {
    debugLog('📦 One-time purchase tab detected');
  }
  
  return isActive;
}

// Global function that will be called from subscriptions_app_addons.liquid
function updatePriceByBundleQty(newQty) {
  debugLog('🔄 updatePriceByBundleQty() called with quantity:', newQty);
  
  // IMPORTANT: Check which tab is active FIRST, before any other logic
  // This determines which pricing logic to apply
  if (isSubscriptionTabActive()) {
    debugLog('📋 Subscription tab is active - using subscription pricing logic');
    debugLog('📋 Calculation: quantity × subscription base price');
    
    // Sync the main quantity input's value and data attribute
    const quantityInput = document.querySelector('.product-info__qty-selector-buy-buttons-wrapper input[is="quantity-input-new-bundles"].quantity-selector__input');
    if (quantityInput) {
      const currentValue = parseInt(quantityInput.value) || 1;
      const newQtyStr = String(newQty);
      // Update only if changed to avoid unnecessary loops
      if (currentValue !== newQty) {
        quantityInput.value = newQtyStr;
        quantityInput.setAttribute('value', newQtyStr);
        quantityInput.setAttribute('data-cart-quantity', newQtyStr);
        quantityInput.dataset.cartQuantity = newQtyStr;
        debugLog('✅ Quantity input synced to:', newQtyStr);
      }
    }
    
    // Update subscription prices (quantity × subscription base price)
    updateSubscriptionPrices(newQty);
    return; // CRITICAL: Exit early to prevent bundle pricing logic from running
  }
  
  debugLog('📦 One-time purchase tab is active - using bundle pricing logic');
  debugLog('📦 Calculation: quantity breaks apply (different prices for 1, 2, 3+ items)');
  
  // Sync the main quantity input's value and data attribute
  const quantityInput = document.querySelector('.product-info__qty-selector-buy-buttons-wrapper input[is="quantity-input-new-bundles"]');
  if (quantityInput) {
    const currentValue = parseInt(quantityInput.value) || 1;
    const newQtyStr = String(newQty);
    // Update only if changed to avoid unnecessary loops
    if (currentValue !== newQty) {
      quantityInput.value = newQtyStr;
      // Keep the DOM attribute in sync for observers reading attributes
      quantityInput.setAttribute('value', newQtyStr);
      quantityInput.setAttribute('data-cart-quantity', newQtyStr);
      quantityInput.dataset.cartQuantity = newQtyStr;
      // Notify any listeners that the quantity changed
      
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      debugLog('✅ Quantity input synced to:', newQtyStr);
    } else {
      // Ensure data attribute stays in sync even if value is the same
      if (quantityInput.getAttribute('data-cart-quantity') !== newQtyStr) {
        quantityInput.setAttribute('data-cart-quantity', newQtyStr);
        quantityInput.dataset.cartQuantity = newQtyStr;
        debugLog('✅ Quantity input data-cart-quantity synced to:', newQtyStr);
      }
    }
  } else {
    debugLog('⚠️ Quantity input not found to sync value');
  }
  
  // ONE-TIME PURCHASE ONLY: Bundle pricing logic with quantity breaks
  // This section only runs when one-time purchase tab is active
  // Ensure the appropriate bundle option is selected based on quantity
  try {
    let targetId = 'prvw__input_1';
    if (newQty === 2) {
      targetId = 'prvw__input_2';
    } else if (newQty >= 3) {
      targetId = 'prvw__input_3';
    }
    const targetInput = document.getElementById(targetId);
    if (targetInput && !targetInput.checked) {
      targetInput.checked = true;
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
      debugLog('✅ Bundle option selected for qty', newQty, '->', targetId);
    }
  } catch (e) {
    debugLog('⚠️ Could not select bundle option for qty:', newQty, e);
  }
  
  try {
    // Get base price from hidden element
    const basePriceElement = document.getElementById('base-price-data');
    if (!basePriceElement) {
      debugLog('❌ Base price element not found');
      return;
    }
    
    const basePrice = parseInt(basePriceElement.dataset.basePrice);
    if (!basePrice || isNaN(basePrice)) {
      debugLog('❌ Invalid base price:', basePriceElement.dataset.basePrice);
      return;
    }
    
    debugLog('💰 Base price:', basePrice);
    
    // Calculate total regular price
    const totalRegularPrice = basePrice * newQty;
    debugLog('💰 Total regular price:', totalRegularPrice);
    
    // Get sales price based on quantity
    const salesPrice = getSalesPriceForQuantity(newQty);
    debugLog('💰 Sales price for quantity', newQty, ':', salesPrice);
    
    // Calculate total sales price
    const totalSalesPrice = salesPrice * newQty;
    debugLog('💰 Total sales price:', totalSalesPrice);
    
    // Update price display
    updatePriceDisplay(totalRegularPrice, totalSalesPrice, newQty);
    
  } catch (error) {
    debugLog('❌ Error in updatePriceByBundleQty:', error);
  }
}

/**
 * Get the sales price per unit based on quantity
 * @param {number} quantity - The quantity selected
 * @returns {number} - The sales price per unit
 */
function getSalesPriceForQuantity(quantity) {
  // Map quantity to the corresponding bundle input element id
  let inputId = 'prvw__input_1';
  if (quantity === 2) {
    inputId = 'prvw__input_2';
  } else if (quantity >= 3) {
    inputId = 'prvw__input_3';
  } else if (quantity !== 1) {
    console.warn('⚠️ Unexpected quantity:', quantity, 'using base price');
    return getBasePrice();
  }

  const inputEl = document.getElementById(inputId);
  if (!inputEl) {
    console.warn('⚠️ Bundle input element not found for ID:', inputId, 'using base price');
    return getBasePrice();
  }

  // Prefer per-each discounted price; fall back to total discounted divided by quantity
  const eachAttr = inputEl.getAttribute('data-price-each-discounted');
  const totalAttr = inputEl.getAttribute('data-price-total-discounted');

  let perUnitCents = null;

  if (eachAttr != null && eachAttr !== '' && !isNaN(parseInt(eachAttr))) {
    perUnitCents = parseInt(eachAttr);
  } else if (totalAttr != null && totalAttr !== '' && !isNaN(parseInt(totalAttr))) {
    const totalCents = parseInt(totalAttr);
    // Guard against divide-by-zero and non-positive quantities
    const safeQty = quantity > 0 ? quantity : 1;
    perUnitCents = Math.round(totalCents / safeQty);
  }

  if (perUnitCents == null) {
    console.warn('⚠️ Discounted price attributes missing/invalid on', inputId, 'falling back to base price');
    return getBasePrice();
  }

  return perUnitCents;
}

/**
 * Extract numeric price value from formatted price text
 * @param {string} priceText - Formatted price text (e.g., "€41,95", "$41.95", "41,95 EUR", "€33,56 EUR")
 * @returns {number|null} - Price in cents, or null if extraction fails
 */
function extractPriceFromText(priceText) {
  if (!priceText) return null;
  
  debugLog('🔍 Extracting price from text:', priceText);
  
  // Remove all currency symbols and "EUR" text
  const cleanText = priceText
    .replace(/[€$£¥₹₽₩₪₫₨₴₸₼₾₿]/g, '') // Remove currency symbols
    .replace(/\s*EUR\s*/gi, '') // Remove "EUR" text
    .trim();
  
  debugLog('🔍 Cleaned text:', cleanText);
  
  // Handle different decimal separators (comma vs period)
  let priceStr;
  if (cleanText.includes(',')) {
    // European format: "41,95" -> 4195
    priceStr = cleanText.replace(',', '');
  } else if (cleanText.includes('.')) {
    // US format: "41.95" -> 4195
    priceStr = cleanText.replace('.', '');
  } else {
    // No decimal separator: "4195" -> 4195
    priceStr = cleanText;
  }
  
  debugLog('🔍 Price string:', priceStr);
  
  const price = parseInt(priceStr);
  const result = isNaN(price) ? null : price;
  
  debugLog('🔍 Extracted price (cents):', result);
  
  return result;
}

/**
 * Get the base price from the hidden element
 * @returns {number} - Base price in cents
 */
function getBasePrice() {
  const basePriceElement = document.getElementById('base-price-data');
  if (!basePriceElement) return 0;
  
  const basePrice = parseInt(basePriceElement.dataset.basePrice);
  return isNaN(basePrice) ? 0 : basePrice;
}

/**
 * Get the units per package from the hidden element
 * @returns {number} - Units per package (can be decimal)
 */
function getPortionsPerPackage() {
  const basePriceElement = document.getElementById('base-price-data');
  if (!basePriceElement) {
    debugLog('⚠️ Base price element not found for units per package');
    return 1;
  }
  
  const portionsPerPackage = parseFloat(basePriceElement.dataset.portionsPerPackage);
  const result = isNaN(portionsPerPackage) ? 1 : portionsPerPackage;
  
  debugLog('📦 Portions per package:', {
    raw: basePriceElement.dataset.portionsPerPackage,
    parsed: portionsPerPackage,
    final: result
  });
  
  return result;
}

/**
 * Calculate unit price based on package price and units per package
 * @param {number} packagePriceInCents - Price per package in cents
 * @returns {number} - Unit price in cents
 */
function calculatePricePerPortion(packagePriceInCents) {
  const portionsPerPackage = getPortionsPerPackage();
  const pricePerPortion = packagePriceInCents / portionsPerPackage;
  
  debugLog('🧮 Price per portion calculation:', {
    packagePriceInCents,
    portionsPerPackage,
    pricePerPortion
  });
  
  return pricePerPortion;
}

/**
 * Update unit price display
 * @param {number} pricePerPortionInCents - Price per portion in cents
 */
function updatePricePerPortionsDisplay(pricePerPortionInCents) {
  const pricePerPortionElement = document.querySelector('.product-info__price .price_per_portion .price-item--per-portion');
  
  if (!pricePerPortionElement) {
    debugLog('⚠️ Price per portion element not found');
    return;
  }

  // Format the unit price
  const formattedPricePerPortion = formatPrice(pricePerPortionInCents);
  
  pricePerPortionElement.textContent = formattedPricePerPortion;
  
  debugLog('✅ Price per portion updated:', formattedPricePerPortion);
  
  /*
  // Get reference value and unit from data attributes
  const basePriceElement = document.getElementById('base-price-data');
  if (!basePriceElement) {
    debugLog('⚠️ Base price element not found');
    return;
  }
  
  const referenceValue = basePriceElement.dataset.unitReferenceValue;
  const referenceUnit = basePriceElement.dataset.unitReferenceUnit;
  
  // Build reference text
  let referenceText = '';
  if (referenceValue && referenceValue !== '1') {
    referenceText += referenceValue;
  }
  if (referenceUnit) {
    referenceText += referenceUnit;
  }
  
  
  // Update only the price span (first span), keep the rest of the structure
  const priceSpan = unitPriceElement.querySelector('span:first-child');
  if (priceSpan) {
    priceSpan.textContent = formattedUnitPrice;
  } else {
    // Fallback: update the entire content
    unitPriceElement.innerHTML = '<span>' + formattedUnitPrice + '</span><span aria-hidden="true">/</span><span class="visually-hidden">&nbsp;' + referenceText + '&nbsp;</span><span>' + referenceText + '</span>';
  }
  
  debugLog('✅ Unit price updated:', formattedUnitPrice + ' / ' + referenceText);
  */
}

/**
 * Format a price with the shop's money format (window.themeVariables.settings.moneyFormat),
 * so the result matches what Liquid's "money" filter renders. Falls back to formatPrice().
 * @param {number} priceInCents - Price in cents
 * @returns {string} - Formatted price string
 */
function formatMoneyShop(priceInCents) {
  const format = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat;
  const match = format ? format.match(/\{\{\s*(\w+)\s*\}\}/) : null;
  if (!match) return formatPrice(priceInCents);

  function withDelimiters(cents, precision, thousands, decimal) {
    const number = (cents / 100).toFixed(precision);
    const parts = number.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  let value;
  switch (match[1]) {
    case 'amount_no_decimals':
      value = withDelimiters(priceInCents, 0, ',', '.');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(priceInCents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(priceInCents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(priceInCents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(priceInCents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(priceInCents, 0, ' ', '.');
      break;
    default:
      value = withDelimiters(priceInCents, 2, ',', '.');
  }
  return format.replace(match[0], value);
}

/**
 * Update the unit price (e.g. €/kg) of the "Price enhanced" block.
 * The unit price scales with the package price: unitPrice = baseUnitPrice * pricePerPackage / basePackagePrice.
 * Discounts (bundle steps, subscription) therefore apply to the unit price as well.
 * @param {number} pricePerPackageInCents - Currently displayed price per package in cents
 */
function updateUnitPriceDisplay(pricePerPackageInCents) {
  const unitPriceElement = document.querySelector('.product-info__price .lun-unit-price');
  if (!unitPriceElement) {
    debugLog('ℹ️ Unit price element (.lun-unit-price) not found, skipping');
    return;
  }

  const baseUnitPrice = parseInt(unitPriceElement.dataset.unitPriceBase);
  const basePackagePrice = parseInt(unitPriceElement.dataset.unitPricePackagePrice);
  const valueElement = unitPriceElement.querySelector('[data-unit-price-value]');

  if (!valueElement || isNaN(baseUnitPrice) || isNaN(basePackagePrice) || basePackagePrice <= 0 || isNaN(pricePerPackageInCents)) {
    debugLog('⚠️ Unit price data incomplete:', { baseUnitPrice, basePackagePrice, pricePerPackageInCents, valueElement: !!valueElement });
    return;
  }

  const newUnitPrice = Math.round((baseUnitPrice * pricePerPackageInCents) / basePackagePrice);
  valueElement.textContent = formatMoneyShop(newUnitPrice);

  debugLog('✅ Unit price updated:', { baseUnitPrice, basePackagePrice, pricePerPackageInCents, newUnitPrice });
}

/**
 * Toggle the tax & shipping notice of the "Price enhanced" block between
 * "plus shipping" and "free shipping" depending on the free shipping threshold.
 * @param {number} displayedTotalPriceInCents - Total price the customer currently sees (after discounts)
 */
function updateShippingNotice(displayedTotalPriceInCents) {
  const notice = document.querySelector('.product-info__price [data-tax-shipping-notice]');
  if (!notice) return;

  const threshold = parseInt(notice.dataset.freeShippingThreshold);
  if (isNaN(threshold) || threshold <= 0) {
    debugLog('ℹ️ No free shipping threshold configured, skipping notice toggle');
    return;
  }

  const defaultText = notice.querySelector('.lun-tax-shipping-notice__text--default');
  const freeShippingText = notice.querySelector('.lun-tax-shipping-notice__text--free-shipping');
  if (!defaultText || !freeShippingText) return;

  const isFreeShipping = displayedTotalPriceInCents >= threshold;
  defaultText.hidden = isFreeShipping;
  freeShippingText.hidden = !isFreeShipping;

  debugLog('✅ Shipping notice updated:', { displayedTotalPriceInCents, threshold, isFreeShipping });
}

/**
 * Format price for display with currency
 * @param {number} priceInCents - Price in cents
 * @returns {string} - Formatted price string
 */
function formatPrice(priceInCents) {
  // Convert cents to main currency unit
  const price = priceInCents / 100;
  
  // Format with 2 decimal places and appropriate separators
  // This is a basic implementation - you may want to use Shopify's money formatting
  return price.toFixed(2).replace('.', ',') + ' EUR';
}

/**
 * Update prices for subscription tab when quantity changes
 * When subscription tab is active, prices are calculated based on:
 * - Strike-through price: base price (variant.price) * quantity
 * - Sale price: subscription base price (with discount applied) * quantity
 * @param {number} quantity - Selected quantity
 */
function updateSubscriptionPrices(quantity) {
  debugLog('📋 Updating subscription prices for quantity:', quantity);
  
  try {
    // Get base price and subscription base price from base-price-data element
    const basePriceElement = document.getElementById('base-price-data');
    
    if (!basePriceElement) {
      debugLog('❌ Base price element not found');
      return;
    }
    
    // Get base price (variant.price) for strike-through price
    const basePriceStr = basePriceElement.getAttribute('data-base-price');
    if (!basePriceStr) {
      debugLog('❌ data-base-price attribute not found');
      return;
    }
    const basePriceInCents = parseInt(basePriceStr);
    if (isNaN(basePriceInCents)) {
      debugLog('❌ Invalid base price:', basePriceStr);
      return;
    }
    
    // Get subscription base price (with discount applied) for sale price
    const subscriptionBasePriceStr = basePriceElement.getAttribute('data-subscription-base-price');
    if (!subscriptionBasePriceStr) {
      debugLog('❌ data-subscription-base-price attribute not found');
      return;
    }
    const subscriptionBasePriceInCents = parseInt(subscriptionBasePriceStr);
    if (isNaN(subscriptionBasePriceInCents)) {
      debugLog('❌ Invalid subscription base price:', subscriptionBasePriceStr);
      return;
    }
    
    debugLog('📋 Price data from base-price-data element:');
    debugLog('  - Base price (for strike-through):', basePriceInCents, 'cents');
    debugLog('  - Subscription base price (for sale):', subscriptionBasePriceInCents, 'cents');
    debugLog('  - Quantity:', quantity);
    
    // Calculate total prices based on quantity
    // Strike-through price uses the regular base price
    const totalRegularPrice = basePriceInCents * quantity;
    // Sale price uses the subscription base price (already discounted)
    const totalSalesPrice = subscriptionBasePriceInCents * quantity;
    
    debugLog('📋 Calculated subscription totals:');
    debugLog('  - Total regular price (strike-through):', totalRegularPrice, 'cents');
    debugLog('  - Total sales price:', totalSalesPrice, 'cents');
    
    // Update price display using the standard function
    // This will show strike-through price and sale price correctly
    updatePriceDisplay(totalRegularPrice, totalSalesPrice, quantity);
    
  } catch (error) {
    debugLog('❌ Error in updateSubscriptionPrices:', error);
  }
}

/**
 * Update the price display in the DOM
 * @param {number} totalRegularPrice - Total regular price in cents
 * @param {number} totalSalesPrice - Total sales price in cents
 * @param {number} quantity - Selected quantity
 */
function updatePriceDisplay(totalRegularPrice, totalSalesPrice, quantity) {
  debugLog('🎨 Updating price display...');
  
  // Find the price wrapper - try multiple selectors for compatibility
  let priceWrapper = document.querySelector('.product-info__price price-list');
  if (!priceWrapper) {
    priceWrapper = document.querySelector('.product-info__price-wrapper price-list');
  }
  if (!priceWrapper) {
    priceWrapper = document.querySelector('price-list');
  }
  if (!priceWrapper) {
    debugLog('❌ Price wrapper not found');
    return;
  }
  
  // Find price container elements
  const priceRegular = priceWrapper.querySelector('.price__regular');
  const priceSale = priceWrapper.querySelector('.price__sale');
  
  if (!priceRegular || !priceSale) {
    debugLog('❌ Price elements not found');
    return;
  }
  
  // Find price item elements
  const regularPriceItem = priceSale.querySelector('.price-item--regular');
  const salePriceItem = priceSale.querySelector('.price-item--sale');
  
  if (!regularPriceItem || !salePriceItem) {
    debugLog('❌ Price item elements not found');
    return;
  }
  
  // Check if we should show sale pricing
  const shouldShowSale = totalSalesPrice < totalRegularPrice;
  
  debugLog('🎨 Should show sale:', shouldShowSale);
  debugLog('🎨 Regular price:', formatPrice(totalRegularPrice));
  debugLog('🎨 Sales price:', formatPrice(totalSalesPrice));
  
  if (shouldShowSale) {
    // Show sale pricing
    priceWrapper.classList.add('price--on-sale');
    priceRegular.classList.add('hidden');
    priceSale.classList.remove('hidden');
    
    // Update price values
    regularPriceItem.textContent = formatPrice(totalRegularPrice);
    salePriceItem.textContent = formatPrice(totalSalesPrice);
    
    // Calculate and update unit price based on sale price per package
    const salePricePerPackage = totalSalesPrice / quantity;
    const pricePerPortion = calculatePricePerPortion(salePricePerPackage);
    updatePricePerPortionsDisplay(pricePerPortion);
    updateUnitPriceDisplay(salePricePerPackage);
    updateShippingNotice(totalSalesPrice);
    
    debugLog('✅ Sale pricing displayed');
  } else {
    // Show regular pricing
    priceWrapper.classList.remove('price--on-sale');
    priceRegular.classList.remove('hidden');
    priceSale.classList.add('hidden');
    
    // Update regular price value
    const regularPriceItemInRegular = priceRegular.querySelector('.price-item--regular');
    if (regularPriceItemInRegular) {
      regularPriceItemInRegular.textContent = formatPrice(totalRegularPrice);
    }
    
    // Calculate and update unit price based on regular price per package
    const regularPricePerPackage = totalRegularPrice / quantity;
    const pricePerPortion = calculatePricePerPortion(regularPricePerPackage);
    updatePricePerPortionsDisplay(pricePerPortion);
    updateUnitPriceDisplay(regularPricePerPackage);
    updateShippingNotice(totalRegularPrice);
    
    debugLog('✅ Regular pricing displayed');
  }
}

/**
 * Set quantity input to 1
 */
function setQuantityToOne() {
  debugLog('🔢 Setting quantity to 1...');
  
  // Try multiple selectors for quantity input
  let quantityInput = document.querySelector('.product-info__qty-selector-buy-buttons-wrapper input[is="quantity-input-new-bundles"]');
  if (!quantityInput) {
    quantityInput = document.querySelector('input[name="quantity"].quantity__input');
  }
  if (!quantityInput) {
    quantityInput = document.querySelector('input[name="quantity"]');
  }
  
  if (quantityInput) {
    const currentValue = parseInt(quantityInput.value) || 1;
    if (currentValue !== 1) {
      quantityInput.value = '1';
      quantityInput.setAttribute('value', '1');
      quantityInput.setAttribute('data-cart-quantity', '1');
      quantityInput.dataset.cartQuantity = '1';
      // Notify any listeners that the quantity changed
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      debugLog('✅ Quantity set to 1');
    } else {
      debugLog('✅ Quantity already set to 1');
    }
  } else {
    debugLog('⚠️ Quantity input not found');
  }
}

/**
 * Update subscription price per portion when subscription tab is clicked
 * Extracts price from subscription radio label and updates the price per portion display
 * Also updates the main price display (regular and sale prices)
 */
function updateSubscriptionPricePerPortion() {
  debugLog('📋 Updating subscription price per portion...');
  
  try {
    // Set quantity to 1 first
    setQuantityToOne();
    
    // Get subscription base price from base-price-data element
    const basePriceElement = document.getElementById('base-price-data');
    let subscriptionBasePriceInCents = null;
    
    if (basePriceElement) {
      const subscriptionBasePrice = basePriceElement.getAttribute('data-subscription-base-price');
      if (subscriptionBasePrice) {
        subscriptionBasePriceInCents = parseInt(subscriptionBasePrice);
        if (isNaN(subscriptionBasePriceInCents)) {
          debugLog('❌ Invalid subscription base price:', subscriptionBasePrice);
          return;
        }
        debugLog('✅ Using subscription base price from data attribute:', subscriptionBasePriceInCents, 'cents');
      } else {
        debugLog('❌ data-subscription-base-price attribute not found');
        return;
      }
    } else {
      debugLog('❌ Base price element not found');
      return;
    }
    
    // Get the regular/base price for comparison
    const regularPriceInCents = getBasePrice();
    debugLog('💰 Regular price:', regularPriceInCents, 'cents');
    debugLog('💰 Subscription price:', subscriptionBasePriceInCents, 'cents');
    
    // Quantity is always 1 for subscriptions
    const quantity = 1;
    
    // Calculate total prices (for quantity = 1)
    const totalRegularPrice = regularPriceInCents * quantity;
    const totalSalesPrice = subscriptionBasePriceInCents * quantity;
    
    // Update the main price display
    updatePriceDisplay(totalRegularPrice, totalSalesPrice, quantity);
    
    // Calculate price per portion using the subscription base price
    const pricePerPortion = calculatePricePerPortion(subscriptionBasePriceInCents);
    
    // Update the price per portion display
    updatePricePerPortionsDisplay(pricePerPortion);
    
    debugLog('✅ Subscription price per portion updated successfully');
    
  } catch (error) {
    debugLog('❌ Error in updateSubscriptionPricePerPortion:', error);
  }
}

/**
 * Update the price display in the DOM with subscription badge
 * @param {number} totalRegularPrice - Total regular price in cents
 * @param {number} totalSalesPrice - Total sales price in cents
 * @param {number} quantity - Selected quantity
 */
function updatePriceDisplayWithSubscription(totalRegularPrice, totalSalesPrice, quantity) {
  debugLog('🎨 Updating subscription price display...');
  
  // Find the price wrapper - try multiple selectors for compatibility
  let priceWrapper = document.querySelector('.product-info__price-wrapper .price');
  if (!priceWrapper) {
    priceWrapper = document.querySelector('.product-info__price price-list');
  }
  if (!priceWrapper) {
    priceWrapper = document.querySelector('.product-info__price .price');
  }
  
  if (!priceWrapper) {
    debugLog('❌ Price wrapper not found');
    return;
  }
  
  // Find price container elements
  const priceRegular = priceWrapper.querySelector('.price__regular');
  const priceSale = priceWrapper.querySelector('.price__sale');
  
  if (!priceRegular || !priceSale) {
    debugLog('❌ Price elements not found');
    return;
  }
  
  // Find price item elements
  const regularPriceItem = priceSale.querySelector('.price-item--regular');
  const salePriceItem = priceSale.querySelector('.price-item--sale');
  
  if (!regularPriceItem || !salePriceItem) {
    debugLog('❌ Price item elements not found');
    return;
  }
  
  // Always show sale pricing for subscriptions (since they have discounts)
  priceWrapper.classList.add('price--on-sale');
  priceRegular.classList.add('hidden');
  priceSale.classList.remove('hidden');
  
  // Update price values
  regularPriceItem.textContent = formatPrice(totalRegularPrice);
  salePriceItem.textContent = formatPrice(totalSalesPrice);
  
  // Calculate and update unit price based on sale price per package (subscriptions always have discounts)
  const salePricePerPackage = totalSalesPrice / quantity;
  const pricePerPortion = calculatePricePerPortion(salePricePerPackage);
  updatePricePerPortionsDisplay(pricePerPortion);
  updateUnitPriceDisplay(salePricePerPackage);
  updateShippingNotice(totalSalesPrice);
  
  // Show subscription badge
  const subscriptionBadge = priceWrapper.querySelector('.price__badge--subscription');
  if (subscriptionBadge) {
    subscriptionBadge.style.display = 'block';
    debugLog('✅ Subscription badge shown');
  } else {
    debugLog('⚠️ Subscription badge not found');
  }
  
  debugLog('✅ Subscription pricing displayed');
}

// Export the main function for global access
window.updatePriceByBundleQty = updatePriceByBundleQty;
window.updateSubscriptionPricePerPortion = updateSubscriptionPricePerPortion;

// Set up quantity input monitoring
function setupQuantityInputMonitoring() {
  const quantityInput = document.querySelector('input[name="quantity"].quantity__input');
  
  if (!quantityInput) {
    debugLog('⏳ Quantity input not found, will retry...');
    return false;
  }
  
  debugLog('✅ Setting up quantity input monitoring');
  
  // Store the last known value
  let lastValue = quantityInput.value;
  
  // Set up MutationObserver to watch for value changes
  const observer = new MutationObserver(() => {
    const currentValue = quantityInput.value;
    if (currentValue !== lastValue) {
      debugLog('🔄 Quantity input value changed via JavaScript:', lastValue, '->', currentValue);
      lastValue = currentValue;
      updatePriceByBundleQty(parseInt(currentValue));
    }
  });
  
  // Watch for attribute changes (value changes)
  observer.observe(quantityInput, {
    attributes: true,
    attributeFilter: ['value']
  });
  
  // Set up polling as fallback for programmatic changes
  const pollInterval = setInterval(() => {
    const currentValue = quantityInput.value;
    if (currentValue !== lastValue) {
      debugLog('🔄 Quantity input value changed via polling:', lastValue, '->', currentValue);
      lastValue = currentValue;
      updatePriceByBundleQty(parseInt(currentValue));
    }
  }, 100); // Check every 100ms
  
  // Store interval ID for potential cleanup
  quantityInput._priceUpdateInterval = pollInterval;
  
  // Also set up direct event listeners as backup
  ['input', 'change', 'keyup', 'paste'].forEach((eventType) => {
    quantityInput.addEventListener(eventType, function(e) {
      debugLog('🔄 Quantity input changed via event:', eventType, 'value:', e.target.value);
      updatePriceByBundleQty(parseInt(e.target.value));
    });
  });
  
  // Set up subscription option change listeners
  setupSubscriptionOptionListeners();
  
  // Set up mutation observer for subscription price changes
  setupSubscriptionPriceObserver();

  // Set up click handler for subscription labels to select first option
  setupSubscriptionLabelClickHandler();
  
  return true;
}

/**
 * Set up event listeners for subscription option changes
 */
function setupSubscriptionOptionListeners() {
  const subscriptionOptions = document.querySelectorAll('.shopify_subscriptions_app_block_label_children input[type="radio"]');
  
  if (subscriptionOptions.length === 0) {
    debugLog('⏳ No subscription options found, will retry...');
    return false;
  }
  
  debugLog('✅ Setting up subscription option listeners for', subscriptionOptions.length, 'options');
  
  subscriptionOptions.forEach((option, index) => {
    option.addEventListener('change', function() {
      if (this.checked) {
        debugLog('📋 Subscription option changed:', index, this.getAttribute('aria-label'));
        
        // Get current quantity
        const quantityInput = document.querySelector('input[name="quantity"].quantity__input');
        const currentQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        
        // Use multiple attempts with delays to handle race conditions
        updateSubscriptionPricesWithRetry(currentQuantity, 0);
      }
    });
  });
  
  return true;
}

/**
 * Update subscription prices with retry mechanism to handle race conditions
 * Now uses data attributes from base-price-data element instead of parsing from subscription options
 * @param {number} quantity - Selected quantity
 * @param {number} attempt - Current attempt number
 */
function updateSubscriptionPricesWithRetry(quantity, attempt) {
  const maxAttempts = 5;
  const delay = attempt * 200; // 0ms, 200ms, 400ms, 600ms, 800ms
  
  debugLog(`🔄 Attempt ${attempt + 1}/${maxAttempts} to update subscription prices (delay: ${delay}ms)`);
  
  setTimeout(() => {
    // Check if subscription tab is still active
    const bundle = document.querySelector('#prvw__bundle_container');
    const subList = document.querySelector('.shopify_subscriptions_app_block_label_children');
    const isSubscriptionTabActive = bundle && bundle.classList.contains('prvw-hidden') && 
                                   subList && subList.classList.contains('prvw-visible');
    
    if (!isSubscriptionTabActive) {
      debugLog('📋 Subscription tab no longer active, stopping retry');
      return;
    }
    
    // Check if base-price-data element exists with required attributes
    const basePriceElement = document.getElementById('base-price-data');
    
    if (!basePriceElement) {
      debugLog('⚠️ Base price element not found on attempt', attempt + 1);
      if (attempt < maxAttempts - 1) {
        updateSubscriptionPricesWithRetry(quantity, attempt + 1);
      }
      return;
    }
    
    // Check if required data attributes are present
    const basePrice = basePriceElement.getAttribute('data-base-price');
    const subscriptionBasePrice = basePriceElement.getAttribute('data-subscription-base-price');
    
    if (!basePrice || !subscriptionBasePrice) {
      debugLog('⚠️ Required price data attributes not found on attempt', attempt + 1, { basePrice, subscriptionBasePrice });
      if (attempt < maxAttempts - 1) {
        updateSubscriptionPricesWithRetry(quantity, attempt + 1);
      }
      return;
    }
    
    // Validate that prices are numeric
    if (isNaN(parseInt(basePrice)) || isNaN(parseInt(subscriptionBasePrice))) {
      debugLog('⚠️ Invalid price data on attempt', attempt + 1, { basePrice, subscriptionBasePrice });
      if (attempt < maxAttempts - 1) {
        updateSubscriptionPricesWithRetry(quantity, attempt + 1);
      }
      return;
    }
    
    // Success! Update the prices
    debugLog('✅ Successfully found price data on attempt', attempt + 1);
    updateSubscriptionPrices(quantity);
    
  }, delay);
}

/**
 * Set up mutation observer to watch for subscription price changes
 */
function setupSubscriptionPriceObserver() {
  const subscriptionContainer = document.querySelector('.shopify_subscriptions_app_block_label_children');
  
  if (!subscriptionContainer) {
    debugLog('⏳ Subscription container not found for observer');
    return;
  }
  
  debugLog('✅ Setting up subscription price mutation observer');
  
  const observer = new MutationObserver((mutations) => {
    let shouldUpdate = false;
    
    mutations.forEach((mutation) => {
      // Check if any subscription input attributes changed
      if (mutation.type === 'attributes' && 
          mutation.target.tagName === 'INPUT' && 
          (mutation.attributeName === 'data-variant-price' || 
           mutation.attributeName === 'data-variant-compare-at-price')) {
        shouldUpdate = true;
        debugLog('🔄 Subscription price attributes changed:', mutation.attributeName);
      }
      
      // Check if new subscription options were added
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && 
              (node.tagName === 'INPUT' || node.querySelector('input[type="radio"]'))) {
            shouldUpdate = true;
            debugLog('🔄 New subscription options added');
          }
        });
      }
    });
    
    if (shouldUpdate) {
      // Check if subscription tab is active
      const bundle = document.querySelector('#prvw__bundle_container');
      const subList = document.querySelector('.shopify_subscriptions_app_block_label_children');
      const isSubscriptionTabActive = bundle && bundle.classList.contains('prvw-hidden') && 
                                     subList && subList.classList.contains('prvw-visible');
      
      if (isSubscriptionTabActive) {
        debugLog('🔄 Subscription prices changed, updating...');
        
        // Get current quantity
        const quantityInput = document.querySelector('input[name="quantity"].quantity__input');
        const currentQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        
        // Update prices with a small delay to ensure all changes are complete
        setTimeout(() => {
          updateSubscriptionPrices(currentQuantity);
        }, 100);
      }
    }
  });
  
  // Observe the subscription container for changes
  observer.observe(subscriptionContainer, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-variant-price', 'data-variant-compare-at-price']
  });
  
  // Store observer for potential cleanup
  subscriptionContainer._subscriptionPriceObserver = observer;
}

/**
 * Set up click handler so that clicking the subscription label selects the first option underneath
 */
function setupSubscriptionLabelClickHandler() {
  // Use event delegation to support dynamically injected elements
  document.addEventListener('click', function(event) {
    // Ignore clicks that occur inside the children list to prevent re-triggering when interacting with radios
    if (event.target && event.target.closest && event.target.closest('.shopify_subscriptions_app_block_label_children')) {
      return;
    }

    const label = event.target && event.target.closest && event.target.closest('.shopify_subscriptions_app_block_label');
    if (!label) return;

    // Only react when the actual div label is the target element (or its descendant)
    if (label.tagName !== 'DIV') return;

    // Find the corresponding children list inside the label (as per actual DOM structure)
    let childrenList = label.querySelector('.shopify_subscriptions_app_block_label_children');

    // If the clicked label does not have an associated children list, ignore this click
    if (!childrenList) {
      debugLog('⚠️ No children list found for clicked subscription label');
      return;
    }

    // Mark this label as active and clear the state from sibling labels within the same container
    const container = label.parentElement || document;
    const activeLabels = container.querySelectorAll('.shopify_subscriptions_app_block_label[data-active="true"]');
    activeLabels.forEach((activeEl) => {
      if (activeEl !== label) activeEl.removeAttribute('data-active');
    });
    label.setAttribute('data-active', 'true');

    const firstInput = childrenList.querySelector('input[type="radio"]');
    if (!firstInput) {
      debugLog('⚠️ No input found inside subscription children list');
      return;
    }

    // Check the first input if not already selected and trigger change
    if (!firstInput.checked) {
      firstInput.checked = true;
      firstInput.dispatchEvent(new Event('change', { bubbles: true }));
      debugLog('✅ First subscription option selected via label click');
    }

    // Ensure prices are updated based on current quantity
    const quantityInput = document.querySelector('input[name="quantity"].quantity__input');
    const currentQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    updateSubscriptionPricesWithRetry(currentQuantity, 0);
  });
}

// Initialize monitoring when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!setupQuantityInputMonitoring()) {
      // Retry if not ready yet
      setTimeout(() => setupQuantityInputMonitoring(), 1000);
    }
  });
} else {
  // DOM is already ready
  if (!setupQuantityInputMonitoring()) {
    // Retry if not ready yet
    setTimeout(() => setupQuantityInputMonitoring(), 1000);
  }
}


document.querySelectorAll('.quantity-selector-new-bundles__button').forEach((button) => {

  let clickedButton = button;
  button.addEventListener('click', function (pointerEvent) {
    const quantityInput = document.querySelector('input[is="quantity-input-new-bundles"]');
    let quantity = parseInt(quantityInput.value);
    if (pointerEvent.currentTarget.dataset.intent === 'decrease') {
      quantity = Math.max(quantity - 1, 1);
    } else if (pointerEvent.currentTarget.dataset.intent === 'increase') {
      quantity += 1;
    }
    quantityInput.value = quantity;
  });
});

debugLog('📦 update-bundle-price.js loaded successfully');

